import { configureStore } from '@reduxjs/toolkit';
import type { TypedUseSelectorHook } from 'react-redux';
import { useDispatch, useSelector } from 'react-redux';

import authSlice from '@/features/auth/authSlice';
import playerSlice from '@/features/player/playerSlice';
import decksSlice from '@/features/player/decksSlice';
import channelsSlice from '@/features/channels/channelsSlice';
import videosSlice from '@/features/videos/videosSlice';
import collectionsSlice from '@/features/collections/collectionsSlice';
import uiSlice from '@/store/uiSlice';

export const store = configureStore({
  reducer: {
    auth: authSlice,
    player: playerSlice,
    decks: decksSlice,
    channels: channelsSlice,
    videos: videosSlice,
    collections: collectionsSlice,
    ui: uiSlice,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [
          'persist/PERSIST',
          'persist/REHYDRATE',
          'player/setCurrentVideo',
          'player/playVideo',
          'decks/enterDjMode',
          'decks/loadDeck',
        ],
        ignoredActionsPaths: [
          'meta.arg',
          'payload.timestamp',
          'payload.publishedAt',
          'payload.dateAdded',
          'payload.createdAt',
          'payload.updatedAt',
        ],
        ignoredPaths: [
          'auth.user.lastLoginAt', 
          'player.currentTime',
          'player.currentVideo.publishedAt',
          'player.currentVideo.dateAdded',
          'player.currentVideo.createdAt',
          'player.currentVideo.updatedAt',
          'player.queue',
          'videos.videosList',
          'collections.likedVideos',
          'decks.decks.a.video',
          'decks.decks.b.video',
        ],
      },
    }),
  devTools: process.env.NODE_ENV !== 'production',
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
